import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getSportIcon } from '../utils/sportEmoji';
import type { Badge } from '../types/activity';

interface BadgeBarProps {
  badges: Badge[];
  side: 'left' | 'right';
}

const TIER_COLORS: Record<number, string> = {
  1: '#e6b800',
  2: '#e6b800',
  3: '#e6b800',
};

function getBadgeDesc(badge: Badge): string {
  const tier = badge.current_tier || 1;
  const val = badge.tier_kriterien?.[tier]?.value ?? 0;
  const type = badge.tier_kriterien?.[tier]?.type ?? '';
  switch (type) {
    case 'weekly_points': return `${val}+ pts/week`;
    case 'win_streak': return `${val} wins in a row`;
    case 'loss_streak': return `${val} losses in a row`;
    case 'sport_groups': return `${val} sport groups (30+ min)`;
    case 'items_used': return `${val} items used/week`;
    case 'weeks_played': return `${val} weeks played`;
    default: return '';
  }
}



export default function BadgeBar({ badges, side }: BadgeBarProps) {
  const earned = Array.isArray(badges) ? badges.filter(b => b.current_tier > 0) : [];
  const [activeBadge, setActiveBadge] = useState<string | null>(null);
  if (earned.length === 0) return null;

  return (
    <div className={`badge-bar badge-bar--${side}`}>
      {/* Desktop: 1 Reihe */}
      <div className="badge-bar-row badge-bar-row--desktop">
        {earned.map(badge => (
          <BadgeIcon key={badge.id} badge={badge} activeBadge={activeBadge} setActiveBadge={setActiveBadge} />
        ))}
      </div>
      {/* Mobile: 2 Reihen à 3 */}
      <div className="badge-bar-grid badge-bar-row--mobile">
        {earned.map(badge => (
          <BadgeIcon key={badge.id} badge={badge} activeBadge={activeBadge} setActiveBadge={setActiveBadge} />
        ))}
      </div>
    </div>
  );
}

function BadgeIcon({ badge, activeBadge, setActiveBadge }: { badge: Badge; activeBadge: string | null; setActiveBadge: (id: string | null) => void }) {
  const [tooltip, setTooltip] = useState<{ x: number; y: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const isTouch = useRef(typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches);
  const tierColor = '#e6b800';
  const reward = badge.tier_rewards[badge.current_tier] ?? 0;
  const isEarned = badge.current_tier > 0;
  const badgeIcon = getSportIcon(badge.icon || 'BadgeReward');
  const isOpen = activeBadge === String(badge.id);

  // Click outside schliesst Tooltip
  useEffect(() => {
    if (!isOpen) { setTooltip(null); return; }
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setActiveBadge(null);
      }
    };
    const scrollHandler = () => setActiveBadge(null);
    document.addEventListener('click', handler, { once: true });
    window.addEventListener('scroll', scrollHandler, { once: true, passive: true });
    return () => {
      document.removeEventListener('click', handler);
      window.removeEventListener('scroll', scrollHandler);
    };
  }, [isOpen, setActiveBadge]);

  function showTT(e: React.MouseEvent) {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setTooltip({ x: rect.left + rect.width / 2, y: rect.top - 8 });
  }
  function hideTT() { setTooltip(null); }
  function toggleTT(e: React.MouseEvent) {
    e.stopPropagation();
    if (isOpen) { setActiveBadge(null); } else { setActiveBadge(String(badge.id)); showTT(e); }
  }

  useEffect(() => {
    if (!isOpen) setTooltip(null);
  }, [isOpen]);

  return (
    <>
      <div
        ref={wrapRef}
        className="badge-icon-wrap"
        onMouseEnter={(e) => { if (!isTouch.current) { setActiveBadge(String(badge.id)); showTT(e); } }}
        onMouseLeave={() => { if (!isTouch.current) { setActiveBadge(null); hideTT(); } }}
        onClick={(e) => isTouch.current ? toggleTT(e) : undefined}
      >
        <div className="badge-icon" style={{
          borderColor: isEarned ? tierColor : '#444',
          background: isEarned ? 'rgba(230,184,0,0.12)' : '#1a1d27',
          color: isEarned ? tierColor : '#444',
        }}>
          <FontAwesomeIcon icon={badgeIcon} />
        </div>
        {isEarned && (
          <span className="item-tier-icons" style={{ color: '#81c784', textAlign: 'center', width: '100%' }}>
            {'▲'.repeat(badge.current_tier)}
          </span>
        )}
      </div>
      {tooltip && ReactDOM.createPortal(
        <div className="badge-tooltip" style={{ position: 'fixed', top: tooltip.y, left: tooltip.x, transform: 'translateX(-50%) translateY(-100%)', zIndex: 9999, pointerEvents: 'none' }}>
          <div className="badge-tooltip-name">{badge.name}</div>
          <div className="badge-tooltip-tier" style={{ color: tierColor }}>Tier {badge.current_tier}</div>
          <div style={{ fontSize: '0.65rem', color: '#ccc', marginTop: 2 }}>{getBadgeDesc(badge)}</div>
          <div className="badge-tooltip-reward">+{reward} pts/week</div>
        </div>,
        document.body
      )}
    </>
  );
}
